import { env } from "@/env";
import { safeSessionGet, safeSessionRemove, safeSessionSet } from "@/lib/browser-storage";
export type AnalyticsProperties = Record<string, unknown>;
type Ga4DataLayerEvent = AnalyticsProperties & {
  event: string;
  analytics_source_event: string;
  ecommerce?: AnalyticsProperties | null;
};
declare global {
  interface Window {
    dataLayer?: unknown[];
    __ishvaramGaInitialized?: boolean;
    gtag?: (...args: unknown[]) => void;
  }
}
const GA4_CUSTOM_PARAM_KEYS = [
  "source",
  "source_path",
  "intent",
  "seo_intent",
  "page_category",
  "customer_problem",
  "urgency",
  "confidence",
  "placement",
  "offer_type",
  "plan_id",
  "product_sku",
  "product_name",
  "product_count",
  "reading_id",
  "message_index",
  "close_reason",
  "sync_status",
  "checkout_channel",
  "commerce_system",
  "agent_trace_id",
  "route_rank_score",
  "route_rank_reason",
  "ranked_offer_order",
  "purchase_intent_score",
  "purchase_intent_band",
  "purchase_intent_reason",
  "intent_taxonomy_version",
  "landing_intent",
  "chat_intent",
  "question_intent",
  "chart_intent",
  "detected_intent",
  "intent_confidence",
  "intent_source",
  "strongest_dosha_id",
  "strongest_dosha_severity",
  "product_affinity",
  "is_primary_cta",
  "cta_rank",
  "paywall_variant",
  "chat_signal_count",
  "is_test_order",
  // Origin + idempotency identifiers. Every event carries `event_source`
  // ("client" | "server") so GA4 + BigQuery can identify which writer
  // fired it — drift-detection without breaking reports. `event_id`
  // mirrors `transaction_id` for purchase/refund-class events so any
  // cross-writer duplicate is detectable in BigQuery.
  "event_source",
  "event_id",
  "client_event_ts",
] as const;
const GA_MEASUREMENT_ID = env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
const GA_SCRIPT_ID = "ga4-direct-script";
const GA_GATEWAY_SCRIPT_SRC = "/metrics/gtag/js";
const GA_FALLBACK_SCRIPT_SRC = "https://www.googletagmanager.com/gtag/js";
export function ensureDataLayer() {
  window.dataLayer ??= [];
  return window.dataLayer;
}
function ensureGtag() {
  const dataLayer = ensureDataLayer();
  window.gtag ??= (...args: unknown[]) => {
    dataLayer.push(args);
  };
  return window.gtag;
}
export function initializeGoogleAnalytics() {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID) return;
  const gtag = ensureGtag();
  if (!window.__ishvaramGaInitialized) {
    gtag("js", new Date());
    gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
    window.__ishvaramGaInitialized = true;
  }
  if (document.getElementById(GA_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.onerror = () => {
    script.onerror = null;
    script.src = `${GA_FALLBACK_SCRIPT_SRC}?id=${GA_MEASUREMENT_ID}`;
  };
  script.src = `${GA_GATEWAY_SCRIPT_SRC}?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}
function analyticsTestOrderEnabled() {
  try {
    const params = new URLSearchParams(window.location.search);
    const testOrder = params.get("test_order");
    if (testOrder === "1" || testOrder === "true") {
      safeSessionSet("ishvaram_test_order", "1");
    }
    if (testOrder === "0" || testOrder === "false") {
      safeSessionRemove("ishvaram_test_order");
    }
    return safeSessionGet("ishvaram_test_order") === "1";
  } catch {
    return false;
  }
}
export function getGoogleAnalyticsClientId() {
  if (typeof document === "undefined") return undefined;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("_ga="));
  if (!cookie) return undefined;

  const value = decodeURIComponent(cookie.slice("_ga=".length));
  const parts = value.split(".");
  if (value.startsWith("GA") && parts.length >= 4) {
    return parts.slice(-2).join(".");
  }
  return value || undefined;
}

/**
 * The GA4 `ga_session_id` for the current browser session, read from the
 * `_ga_<measurement-id>` session cookie. Required so a server-side
 * Measurement Protocol `purchase` (fired from /payment/verify or the Razorpay
 * webhook) stitches to THIS session — without it, GA4 opens a fresh
 * (direct)/(none) session for every conversion. Handles both cookie shapes:
 *   GS1.1.<sessionId>.<sessionNumber>.…        (legacy)
 *   GS2.1.s<sessionId>$o<n>$g<n>$t<ts>$…        (2024+)
 */
export function getGoogleAnalyticsSessionId(): string | undefined {
  if (typeof document === "undefined" || !GA_MEASUREMENT_ID) return undefined;
  const cookieName = `_ga_${GA_MEASUREMENT_ID.replace(/^G-/, "")}`;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${cookieName}=`));
  if (!cookie) return undefined;
  const value = decodeURIComponent(cookie.slice(cookieName.length + 1));
  const v2 = value.match(/[.$]s(\d{6,})/); // GS2: …$s<sessionId>… (or ".s")
  if (v2) return v2[1];
  const parts = value.split("."); // GS1: GS1.1.<sessionId>.<sessionNumber>.…
  if (parts.length >= 3 && /^\d{6,}$/.test(parts[2])) return parts[2];
  return undefined;
}
export function withTestOrderFlag(properties?: AnalyticsProperties) {
  if (!analyticsTestOrderEnabled()) return properties;
  return { ...properties, is_test_order: true };
}
export function isTestOrder(properties?: AnalyticsProperties) {
  return properties?.is_test_order === true || properties?.is_test_order === "true";
}
function numberParam(value: unknown) {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}
function stringParam(value: unknown) {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}
function arrayParam(value: unknown) {
  return Array.isArray(value) ? value : undefined;
}
function pickGa4CustomParams(properties: AnalyticsProperties = {}) {
  const params: AnalyticsProperties = {};
  for (const key of GA4_CUSTOM_PARAM_KEYS) {
    const value = properties[key];
    if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  }
  return params;
}
function reportItem(properties: AnalyticsProperties = {}) {
  const planId = stringParam(properties.plan_id) ?? "paid_report";
  return {
    item_id: planId,
    item_name: planId,
    item_category: "report",
  };
}
function productItem(properties: AnalyticsProperties = {}) {
  const sku = stringParam(properties.product_sku) ?? "shopify_product";
  return {
    item_id: sku,
    item_name: stringParam(properties.product_name) ?? sku,
    item_category: "shopify_product",
  };
}
function productSkuItems(properties: AnalyticsProperties = {}) {
  const skus = arrayParam(properties.product_skus);
  if (!skus?.length) return [productItem(properties)];
  return skus.map((sku) => ({
    item_id: String(sku),
    item_name: String(sku),
    item_category: "shopify_product",
  }));
}
function revenueItems(properties: AnalyticsProperties = {}) {
  if (properties.product_sku) return [productItem(properties)];
  return [reportItem(properties)];
}
function commerceEvent(
  event: string,
  sourceEvent: string,
  properties: AnalyticsProperties,
  ecommerce: AnalyticsProperties
): Ga4DataLayerEvent {
  return {
    event,
    analytics_source_event: sourceEvent,
    ...pickGa4CustomParams(properties),
    ecommerce,
  };
}
function promotionName(properties: AnalyticsProperties = {}) {
  return stringParam(properties.offer_type) ?? stringParam(properties.plan_id) ?? "revenue_offer";
}
export function commerceOfferProperties(properties: AnalyticsProperties = {}) {
  return {
    ...properties,
    offer_type:
      stringParam(properties.offer_type) ??
      (properties.product_sku || properties.product_skus ? "product" : "report"),
  };
}
function ga4EventFor(name: string, properties: AnalyticsProperties = {}) {
  return GA4_EVENT_BUILDERS[name]?.(name, ga4EventContext(properties)) ?? null;
}

function ga4EventContext(properties: AnalyticsProperties = {}) {
  return {
    properties,
    price: numberParam(properties.price_inr),
    currency: stringParam(properties.currency) ?? "INR",
  };
}

type Ga4EventContext = ReturnType<typeof ga4EventContext>;
type Ga4EventBuilder = (name: string, context: Ga4EventContext) => Ga4DataLayerEvent | null;

const GA4_EVENT_BUILDERS: Record<string, Ga4EventBuilder> = {
  report_checkout_started: beginCheckoutEvent,
  commerce_checkout_started: beginCheckoutEvent,
  commerce_add_to_cart: commerceAddToCartEvent,
  paid_cta_clicked: selectPromotionEvent,
  agent_revenue_offer_clicked: selectPromotionEvent,
  report_remedy_kit_upsell_clicked: selectPromotionEvent,
  commerce_offer_clicked: selectPromotionEvent,
  paid_report_nudge_shown: viewPromotionEvent,
  agent_revenue_offer_shown: viewPromotionEvent,
  report_remedy_kit_upsell_shown: viewPromotionEvent,
  commerce_offer_shown: viewPromotionEvent,
  seo_agent_bridge_clicked: seoAgentBridgeEvent,
  seo_intent_captured: generateLeadEvent,
  contact_captured: generateLeadEvent,
  shopify_product_clicked: shopifyProductClickedEvent,
  shopify_cart_started: shopifyCartStartedEvent,
  revenue_completed: () => null,
};

function beginCheckoutEvent(name: string, context: Ga4EventContext) {
  return commerceEvent("begin_checkout", name, context.properties, {
    currency: context.currency,
    value: context.price,
    items: [reportItem(context.properties)],
  });
}

function commerceAddToCartEvent(name: string, context: Ga4EventContext) {
  return commerceEvent("add_to_cart", name, context.properties, {
    currency: context.currency,
    items: productOrRevenueItems(context.properties),
  });
}

function selectPromotionEvent(name: string, context: Ga4EventContext) {
  return commerceEvent("select_promotion", name, context.properties, {
    promotion_name: promotionName(context.properties),
    items: productOrReportItems(context.properties),
  });
}

function viewPromotionEvent(name: string, context: Ga4EventContext) {
  return commerceEvent("view_promotion", name, context.properties, {
    promotion_name: promotionName(context.properties),
    items: productOrReportItems(context.properties),
  });
}

function seoAgentBridgeEvent(name: string, context: Ga4EventContext) {
  return {
    event: "select_content",
    analytics_source_event: name,
    content_type: "seo_agent_bridge",
    item_id: stringParam(context.properties.target) ?? promotionName(context.properties),
    ...pickGa4CustomParams(context.properties),
  };
}

function generateLeadEvent(name: string, context: Ga4EventContext) {
  return {
    event: "generate_lead",
    analytics_source_event: name,
    ...pickGa4CustomParams(context.properties),
  };
}

function shopifyProductClickedEvent(name: string, context: Ga4EventContext) {
  return commerceEvent("select_item", name, context.properties, {
    items: [productItem(context.properties)],
  });
}

function shopifyCartStartedEvent(name: string, context: Ga4EventContext) {
  return commerceEvent("add_to_cart", name, context.properties, {
    currency: context.currency,
    items: productSkuItems(context.properties),
  });
}

function productOrRevenueItems(properties: AnalyticsProperties) {
  return properties.product_skus ? productSkuItems(properties) : revenueItems(properties);
}

function productOrReportItems(properties: AnalyticsProperties) {
  return properties.product_skus ? productSkuItems(properties) : [reportItem(properties)];
}
function ga4DirectParams(mappedEvent: Ga4DataLayerEvent) {
  const { event: _event, ecommerce, ...params } = mappedEvent;
  return ecommerce ? { ...params, ...ecommerce } : params;
}
export function ga4RawParams(name: string, properties: AnalyticsProperties = {}) {
  const params: AnalyticsProperties = { analytics_source_event: name };
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      params[key] = value;
      continue;
    }
    if (Array.isArray(value)) {
      params[key] = value.join(",");
    }
  }
  return params;
}
function pushDirectGa4Event(mappedEvent: Ga4DataLayerEvent) {
  if (!GA_MEASUREMENT_ID) return;
  initializeGoogleAnalytics();
  window.gtag?.("event", mappedEvent.event, ga4DirectParams(mappedEvent));
}
export function pushDirectRawGa4Event(name: string, properties?: AnalyticsProperties) {
  if (!GA_MEASUREMENT_ID) return;
  initializeGoogleAnalytics();
  window.gtag?.("event", name, ga4RawParams(name, properties));
}
export function pushGa4Event(name: string, properties?: AnalyticsProperties) {
  const mappedEvent = ga4EventFor(name, properties);
  if (!mappedEvent) return;
  const dataLayer = ensureDataLayer();
  if (mappedEvent.ecommerce) {
    dataLayer.push({ ecommerce: null });
  }
  dataLayer.push(mappedEvent);
  pushDirectGa4Event(mappedEvent);
}
